import React from 'react';
import './DashboardOpenInvoices.scss';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useQuery } from '@apollo/client/react';
import { GET_OPEN_INVOICE_AGE } from '../../graphql/client/queries';
import { useStoreContext } from '../App/StoreProvider';
import { GetOpenInvoiceAge } from '../../types/schemaTypes';
import { Loading } from '../_common';

const DashboardOpenInvoices = (): React.JSX.Element => {
    const [state] = useStoreContext();
    const {
        customerList: { customer },
    } = state;

    const variables = {
        input: {
            searchString: '',
            customer,
            addressNumber: 0,
            invoiceStatus: 1,
            pageSize: 0,
            offSet: 0,
            sortParam: 'invoiceDate',
            sortDirection: 'desc',
            invoiceNumber: '',
            poNumber: '',
        },
    };

    const { data, loading } = useQuery<GetOpenInvoiceAge>(GET_OPEN_INVOICE_AGE, {
        variables,
        skip: customer === 0,
        fetchPolicy: 'network-only',
    });

    const invoiceList: { OpenAmount: number; Range: string }[] = [];
    if (data?.getOpenInvoiceAge) {
        const { overSixty, thirtyOneSixty, sixteenThirty, oneFifteen, current } = data.getOpenInvoiceAge;
        invoiceList.push({ OpenAmount: overSixty ?? 0, Range: 'Over 60' });
        invoiceList.push({ OpenAmount: thirtyOneSixty ?? 0, Range: '31 to 60' });
        invoiceList.push({ OpenAmount: sixteenThirty ?? 0, Range: '16 to 30' });
        invoiceList.push({ OpenAmount: oneFifteen ?? 0, Range: '1 to 15' });
        invoiceList.push({ OpenAmount: current ?? 0, Range: 'Current' });
    }

    const formatXAxis = (tickValue: number) => {
        if (tickValue == 8500000) return `>$${tickValue}`;
        return `$${tickValue}`;
    };

    return (
        <div className="Dashboard-OpenInvoices-Container">
            <div className="Dashboard-OpenInvoices-SearchBar">
                <label className="OpenInvoices-Headings" htmlFor="staticEmail">
                    &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Open Invoices — Aging Summary
                </label>
                <div>
                    {customer != 0 && <div className="Loading-Class">{loading && <Loading />}</div>}
                    <ResponsiveContainer width={520} height={180} className="Chart-Class">
                        <BarChart
                            data={invoiceList}
                            layout="vertical"
                            margin={{ top: 20, bottom: 15 }}
                        >
                            <Bar dataKey="OpenAmount" fill="gray" />
                            <CartesianGrid horizontal={false} />
                            <XAxis
                                type="number"
                                dataKey="OpenAmount"
                                style={{ textAnchor: 'middle', fontSize: '10px' }}
                                interval={0}
                                domain={[0, 8700000]}
                                angle={40}
                                dx={15}
                                dy={15}
                                ticks={[0, 500000, 1000000, 1500000, 2000000, 2500000, 3000000, 3500000, 4000000, 4500000, 5000000, 5500000, 6000000, 6500000, 7000000, 7500000, 8000000]}
                                tickFormatter={(tickValue) => formatXAxis(tickValue).replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,')}
                                tickCount={8}
                            />
                            <YAxis
                                type="category"
                                dataKey="Range"
                                interval={0}
                                width={50}
                                style={{ textAnchor: 'end', fontSize: '10px', fill: 'black' }}
                            />
                            <Tooltip />
                        </BarChart>
                    </ResponsiveContainer>
                    <label className="xaxislabel">Open Amount</label>
                    <label className="yaxislabel">Days Beyond Payment Terms</label>
                </div>
            </div>
        </div>
    );
};

export default DashboardOpenInvoices;
